/**
 * document.js
 */

import React from 'react';
import { useDrag } from 'react-dnd';
import {
  useHistory,
} from "react-router-dom";
import makeStyles from '@mui/styles/makeStyles';
import {
  Typography,
  Box,
  IconButton,
  TableCell,
  TableRow,
} from '@mui/material';
import { makeLocation } from 'utils/routing';
import Icon from 'components/icon';
import DropMenu from 'components/drop-menu';
import FileTypeIcon from 'components/file-type-icon';
import { itemTypes } from './models';


const useStyles = makeStyles(theme => ({
  row: {
    '& > *:first-child': {
      paddingLeft: 0
    },
    '& > *:last-child': {
      paddingRight: 0,
    },
  },
  fileIcon: {
    minHeight: theme.spacing(3.5),
    minWidth: theme.spacing(3.5),
    marginLeft: theme.spacing(0),
    marginRight: theme.spacing(2),
  },
  fileName: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    userSelect: 'none',
  },
  rowCells: {
    maxWidth: 0,
  },
  actionsCell: {
    paddingTop: 0,
    paddingBottom: 0,
    width: '1%',
  },
  dragIcon: {
    display: 'flex',
    color: theme.palette.grey.colors[7],
    cursor: 'grab',
    padding: '10px 10px 10px 0px',
  },
}));



export function DocumentDisplay(props) {
  const {
    file,
    category,
    editFilename,
    deleteFile,
    dragPreview,
    style,    
    dragArray,
  } = props;
  const classes = useStyles();
  const history = useHistory();

  let opacity, drag, preview;
  if (dragArray) {
    [{ opacity }, drag, preview] = dragArray;
  }


  const openFile = () => {
    if (file.url) {
      window.open(file.url, '_blank');
    }
  }


  const menuItems = [
    {
      key: 'open',
      name: 'Open File',
      icon: 'open-in-tab',
      onSelect: () => openFile()
    },
  ];
  if (editFilename) {
    menuItems.push({
      key: 'rename',
      name: 'Rename ...',
      icon: 'edit',
      onSelect: () => editFilename(file, category)
    });
  }
  if (deleteFile) {
    menuItems.push({
      key: 'delete',
      name: 'Delete ...',
      icon: 'delete',
      onSelect: () => deleteFile(file, category)
    });
  }
  if (file.vetVisitId) {
    menuItems.push({
      key: 'vetVisit',
      name: 'Open Vet Visit',
      icon: 'vet-visit',
      onSelect: () => history.push(makeLocation(
        '/records',
        'vetVisit',
        'view',
        { vetVisitId: file.vetVisitId }        
      ))
    });
  }


  return (
    <TableRow
      style={{ opacity }}
      className={classes.row}
      hover>
      <TableCell
        component="th"
        scope="row"
        ref={preview}
        onClick={openFile}
        className={classes.rowCells}
        style={{ ...style }}
      >
        <Box
          display="flex"
          alignItems="center"
          title="Open file"
        >
          <Box 
            className={classes.dragIcon} 
            ref={drag} 
            title='Drag and drop files between categories'
            style={{ visibility: drag ? 'visible' : 'hidden' }}
          >
            <Icon type='drag' />
          </Box>
          <FileTypeIcon className={classes.fileIcon} filename={file.filename} />
          <Box>
            <Typography className={classes.fileName}>
              {file.filename.split('.').slice(0, -1).join('.')}
            </Typography>
            {file.subtitle &&
              <Typography variant='subtext-small' component='div'>
                {file.subtitle}
              </Typography>
            }
          </Box>
        </Box>
      </TableCell>

      {/* actions */}
      {!dragPreview &&
        <TableCell
          align="right"
          className={classes.actionsCell}
          style={{ ...style }}
        >
          <Box
            display="flex"
            alignItems="center"
          >
            <DropMenu
              id={`${category}-file-menu`}
              aria-label="file menu options"
              label={<IconButton size='small'><Icon type='menu' /></IconButton>}
              items={menuItems}
            />
          </Box>
        </TableCell>}
    </TableRow>
  );
}


export default function Document(props) {
  const {
    file,
    category,
    editFilename,
    deleteFile,
    dragPreview,
    style
  } = props;
  const dragArray = useDrag({
    item: { file: file },
    type: itemTypes.FILE,
    collect: (monitor) => ({
      opacity: monitor.isDragging() ? 0.4 : 1,
    }),
  });


  return <DocumentDisplay
    {...{
      file,
      category,
      editFilename,
      deleteFile,
      dragPreview,
      style,
      dragArray,
    }}
  />;

}